// Turns: advance the date by one week and raise what happened in it.
//
//   nextTurn()   date D ──► D+7 (clamped to as-of)           the map moves at once
//                  │
//                  └─ queue ─► (D, D+7]  world.changes  +  world.events(D+1, D+7)
//                                 │
//                                 └─► scored (notifications.ts) ─► cards + log
//
// Results are raised in turn order (one queue), so a fast autoplay never posts week 12 before
// week 11. A jump (slider, another leader) bumps `epoch`; a turn still in flight for an older
// epoch is dropped, because its notifications belong to a different place in time.

import { addDays } from '../core/dates.ts';
import type { DateStr, LeaderId, WorldEvent } from '../core/schema.ts';
import type { WorldSnapshot } from '../world/api.ts';
import type { WorldChange } from '../world/changes.ts';
import { notice, post, turnNotes, type Viewer } from './notifications.ts';
import type { Store } from './state.ts';

/** One turn is one week (ARCHITECTURE §3.5). */
export const TURN_DAYS = 7;

/** The parts of the world a turn reads. */
export interface TurnWorld {
  changes(from: DateStr, to: DateStr, leader: LeaderId): WorldChange[];
  /** Both ends inclusive. */
  events(from: DateStr, to: DateStr): Promise<WorldEvent[]>;
  snapshot(date: DateStr, leader: LeaderId): WorldSnapshot;
  actorIndex(id: string): number;
  source(id: string): unknown;
  /** The leader's state. */
  selfOf(leader: LeaderId): string;
}

export interface Turns {
  /** Advance one turn and raise its notifications; resolves once they are posted (or dropped). */
  next(): Promise<void>;
  /** Raise the notifications of the turn ending at `to`, without moving the date. */
  catchUp(to: DateStr): Promise<void>;
  /** The date or leader jumped: turns in flight are dropped. */
  jumped(): void;
}

export function createTurns(store: Store, world: TurnWorld): Turns {
  let epoch = 0;
  let queue: Promise<void> = Promise.resolve();

  function viewer(leader: LeaderId, date: DateStr): Viewer {
    return {
      iso3: world.selfOf(leader),
      snapshot: world.snapshot(date, leader),
      indexOf: (id) => world.actorIndex(id),
      hasSource: (id) => world.source(id) !== null,
    };
  }

  /** Raise (from, to] for `leader`, unless a jump happened since `stamp`. */
  async function raise(from: DateStr, to: DateStr, leader: LeaderId, stamp: number): Promise<void> {
    let events: WorldEvent[] = [];
    let failure: string | null = null;

    try {
      events = await world.events(addDays(from, 1), to);
    } catch (err) {
      failure = err instanceof Error ? err.message : String(err);
    }
    if (stamp !== epoch || store.leader.peek() !== leader) {
      return;
    }

    const notes = turnNotes(viewer(leader, to), world.changes(from, to, leader), events);
    if (failure !== null) {
      const missing = notice(`evt:missing:${from}:${to}`, to, `Events for ${from} to ${to} could not be loaded`, failure);
      notes.cards.push(missing);
      notes.log.push(missing);
    }
    post(store, notes);
  }

  function enqueue(from: DateStr, to: DateStr): Promise<void> {
    const leader = store.leader.peek();
    if (!leader) {
      return Promise.resolve();
    }
    const stamp = epoch;

    // One failing turn must not stall the ones behind it.
    queue = queue.then(() => raise(from, to, leader, stamp)).catch((err: unknown) => {
      console.error(`turns: (${from}, ${to}] failed: ${String(err)}`);
    });
    return queue;
  }

  return {
    next() {
      const from = store.date.peek();
      const asOf = store.asOf.peek();
      if (from >= asOf) {
        return Promise.resolve();
      }
      const to = addDays(from, TURN_DAYS) > asOf ? asOf : addDays(from, TURN_DAYS);
      store.date.value = to;
      return enqueue(from, to);
    },

    catchUp(to) {
      return enqueue(addDays(to, -TURN_DAYS), to);
    },

    jumped() {
      epoch += 1;
    },
  };
}
